import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { parseRRuleString } from '../utils/rruleDateTime';
import {
  ScheduleUpdateScope,
  type HandleOpenCreateModalOptions,
  type MachineDetailsRobotScheduleContextValue,
  type MachineDetailsRobotSchedulePeriod,
  type UseMachineDetailsRobotScheduleStateParams,
} from '../types/MachineDetailsRobotSchedule.types';
import { getCurrentWeekPeriod, getWeekEnd, getWeekStart } from '../utils/period';
import { isScheduleDateTimeInThePast } from '../utils/schedule';
import { RobotCalendarEvent } from '../components/RobotSchedule/RobotSchedule.types';
import {
  selectDeleteScheduleData,
  selectDeleteScheduleIsLoading,
  selectScheduleMaps,
  selectScheduleMapsIsLoading,
  selectUpdateScheduleData,
} from '../../../../state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';
import { RobotUtils } from 'app/utils/robot/RobotUtils';
import { RobotScheduleActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSlice';
import { MachineConnectionStatus } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { toast } from 'app/cross-cutting-concerns/toasts/kcc/toast';

export const useMachineDetailsRobotScheduleState = ({
  robot,
}: UseMachineDetailsRobotScheduleStateParams): MachineDetailsRobotScheduleContextValue => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deleteScheduleData = useSelector(selectDeleteScheduleData);
  const isDeleteScheduleLoading = useSelector(selectDeleteScheduleIsLoading);
  const scheduleMaps = useSelector(selectScheduleMaps);
  const isScheduleMapsLoading = useSelector(selectScheduleMapsIsLoading);
  const updateScheduleData = useSelector(selectUpdateScheduleData);

  const [period, setPeriod] = useState<MachineDetailsRobotSchedulePeriod>(getCurrentWeekPeriod);
  const [today, setToday] = useState<string>(dayjs().toISOString());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isMapAssignmentModalOpen, setIsMapAssignmentModalOpen] = useState<boolean>(false);
  const [isUpdateConfirmModalOpen, setIsUpdateConfirmModalOpen] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<RobotCalendarEvent | null>(null);
  const [selectedCreateDateTime, setSelectedCreateDateTime] = useState<Dayjs | null>(null);
  const [isScheduleCreateFullSizeOpen, setIsScheduleCreateFullSizeOpen] = useState<boolean>(false);
  const [hasOverlap, setHasOverlap] = useState<boolean>(false);
  const [updateScope, setUpdateScope] = useState<ScheduleUpdateScope>(ScheduleUpdateScope.ThisEventOnly);
  const [deleteScope, setDeleteScope] = useState<ScheduleUpdateScope>(ScheduleUpdateScope.ThisEventOnly);
  const isB50 = RobotUtils.getRobotType(robot.type?.name) === 'B50';
  const isRobotOnline = robot.connectionStatus === MachineConnectionStatus.Online;
  const isRobotVersionSupported = useMemo(
    () => !isB50 || RobotUtils.isScheduleSupportedVersion(robot.softwareVersion),
    [isB50, robot.softwareVersion]
  );
  const isSelectedEventInThePast = Boolean(
    selectedEvent && isScheduleDateTimeInThePast(selectedEvent.resource.scheduleEvent.occurrence.startDate)
  );
  const isReadOnly = isEditModalOpen && (!isRobotOnline || !isRobotVersionSupported || isSelectedEventInThePast);
  const weekNumber = useMemo(() => period.startDate.week(), [period.startDate]);
  const isSelectedEventRepeating = useMemo(() => {
    if (!selectedEvent) {
      return false;
    }

    const { count } = parseRRuleString(selectedEvent.resource.scheduleEvent.occurrence.rrule);

    return count !== 1;
  }, [selectedEvent]);

  const handlePeriodChange = useCallback((dates: [Dayjs | null, Dayjs | null] | null): void => {
    const selectedWeek = dates?.[0];

    if (!selectedWeek) {
      return;
    }

    setPeriod({
      startDate: getWeekStart(selectedWeek),
      endDate: getWeekEnd(selectedWeek),
    });
  }, []);

  const handlePreviousWeek = useCallback((): void => {
    setPeriod(current => {
      const previousWeekStart = current.startDate.subtract(1, 'week');

      return {
        startDate: getWeekStart(previousWeekStart),
        endDate: getWeekEnd(previousWeekStart),
      };
    });
  }, []);

  const handleNextWeek = useCallback((): void => {
    setPeriod(current => {
      const nextWeekStart = current.startDate.add(1, 'week');

      return {
        startDate: getWeekStart(nextWeekStart),
        endDate: getWeekEnd(nextWeekStart),
      };
    });
  }, []);

  const handleToday = useCallback((): void => {
    setToday(dayjs().local().toISOString());
    setPeriod(getCurrentWeekPeriod());
  }, []);

  const handleOpenCreateModal = useCallback((options: HandleOpenCreateModalOptions = {}): void => {
    setSelectedCreateDateTime(options.dateTime ? dayjs(options.dateTime) : null);
    setIsCreateModalOpen(true);
    setIsScheduleCreateFullSizeOpen(options.isScheduleCreateFullSizeOpen ?? false);
    setHasOverlap(options.hasEventOverlap ?? false);
  }, []);

  const handleCreateScheduleClick = useCallback((): void => {
    if (isScheduleMapsLoading) {
      return;
    }

    if (isB50 || (scheduleMaps?.length ?? 0) > 0) {
      handleOpenCreateModal({ isScheduleCreateFullSizeOpen: true });
      return;
    }

    setIsMapAssignmentModalOpen(true);
  }, [isB50, scheduleMaps, isScheduleMapsLoading, handleOpenCreateModal]);

  const handleMapAssignmentAssigned = useCallback((): void => {
    setIsMapAssignmentModalOpen(false);
    handleOpenCreateModal({ isScheduleCreateFullSizeOpen: true });
  }, [handleOpenCreateModal]);

  const handleCloseMapAssignmentModal = useCallback((): void => {
    setIsMapAssignmentModalOpen(false);
  }, []);

  const handleOpenEditModal = useCallback((event: RobotCalendarEvent): void => {
    setIsEditModalOpen(true);
    setSelectedEvent(event);
  }, []);

  const handleCloseModal = useCallback((): void => {
    if (isEditModalOpen) {
      setIsEditModalOpen(false);
    } else if (isCreateModalOpen) {
      setIsCreateModalOpen(false);
    } else {
      setIsDeleteModalOpen(false);
    }
  }, [isEditModalOpen, isCreateModalOpen]);

  const handleAfterCloseModal = useCallback((): void => {
    setIsScheduleCreateFullSizeOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedCreateDateTime(null);
    setHasOverlap(false);

    if (!isEditModalOpen) {
      setSelectedEvent(null);
    }
  }, [isEditModalOpen]);

  const handleOpenDeleteModal = useCallback((): void => {
    if (selectedEvent && isScheduleDateTimeInThePast(selectedEvent.start)) {
      toast.error({
        message: t('machineDetails.robotSchedule.deleteForbiddenInThePast'),
        className: 'kcc-toast--message-regular',
      });
      return;
    }

    setDeleteScope(ScheduleUpdateScope.ThisEventOnly);
    setIsDeleteModalOpen(true);
  }, [selectedEvent, t]);

  const handleCloseDeleteModal = useCallback((): void => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleDeleteScopeChange = useCallback((scope: ScheduleUpdateScope): void => {
    setDeleteScope(scope);
  }, []);

  const handleDelete = useCallback((): void => {
    const scheduleEvent = selectedEvent?.resource.scheduleEvent;
    const occurrence = scheduleEvent?.occurrence;

    if (!scheduleEvent || !occurrence) {
      return;
    }

    if (isScheduleDateTimeInThePast(occurrence.startDate)) {
      toast.error({
        message: t('machineDetails.robotSchedule.deleteForbiddenInThePast'),
        className: 'kcc-toast--message-regular',
      });
      return;
    }

    if (isB50) {
      dispatch(
        RobotScheduleActions.deleteScheduleRequest({
          id: occurrence.scheduleId,
          occurrence: occurrence.startDate,
          applyToFollowing: isSelectedEventRepeating && deleteScope === ScheduleUpdateScope.ThisAndFollowing,
        })
      );
      return;
    }

    dispatch(RobotScheduleActions.deleteScheduleRequest({ id: occurrence.scheduleId }));
  }, [dispatch, selectedEvent, isB50, isSelectedEventRepeating, deleteScope, t]);

  const handleOpenUpdateConfirmModal = useCallback((): void => {
    setIsUpdateConfirmModalOpen(true);
  }, []);

  const handleCloseUpdateConfirmModal = useCallback((): void => {
    setIsUpdateConfirmModalOpen(false);
    setUpdateScope(ScheduleUpdateScope.ThisEventOnly);
  }, []);

  const handleUpdateScopeChange = useCallback((scope: ScheduleUpdateScope): void => {
    setUpdateScope(scope);
  }, []);

  useEffect(() => {
    if (!updateScheduleData) {
      return;
    }

    handleCloseUpdateConfirmModal();
    setIsDeleteModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedEvent(null);
    dispatch(RobotScheduleActions.updateScheduleReset());
  }, [updateScheduleData, dispatch, handleCloseUpdateConfirmModal]);

  useEffect(() => {
    if (!deleteScheduleData) {
      return;
    }

    setIsDeleteModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedEvent(null);
    dispatch(RobotScheduleActions.deleteScheduleReset());
  }, [deleteScheduleData, dispatch]);

  return {
    robot,
    period,
    weekNumber,
    handlePeriodChange,
    handlePreviousWeek,
    handleNextWeek,
    handleToday,
    today,
    isB50,
    isRobotOnline,
    isRobotVersionSupported,
    isReadOnly,
    isCreateModalOpen,
    isEditModalOpen,
    isScheduleCreateFullSizeOpen,
    isDeleteModalOpen,
    isMapAssignmentModalOpen,
    isUpdateConfirmModalOpen,
    selectedEvent,
    selectedCreateDateTime,
    hasOverlap,
    isSelectedEventRepeating,
    updateScope,
    deleteScope,
    isDeleteScheduleLoading,
    isScheduleMapsLoading,
    handleOpenCreateModal,
    handleOpenEditModal,
    handleOpenUpdateConfirmModal,
    handleCloseUpdateConfirmModal,
    handleUpdateScopeChange,
    handleCloseModal,
    handleAfterCloseModal,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleDeleteScopeChange,
    handleDelete,
    handleCreateScheduleClick,
    handleMapAssignmentAssigned,
    handleCloseMapAssignmentModal,
  };
};
